import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Table,
  Tag,
  Typography,
} from "antd";
import React, { useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Actions from "../../../../../redux/actions";
import Spinner from "../../../../common/spinner/Spinner";
import {
  dateObjToDispViewDate,
  timeObjToDispViewTimeWithSuffix,
  timeStrToDispViewTimeWithSuffix,
} from "../../../../../utils/utils";
import { appConstConfig } from "../../../../../constants/config/appConstConfig";
import { colors } from "../../../../../constants/colors";
import { CalendarOutlined } from "@ant-design/icons";
import {
  getAvailableStaffForAppointmentAction,
  getServiceProviderAppointmentsAction,
} from "../../../../../redux/services/service-provider-appointments/serviceProviderAppointmentsServices";
import useDebounce from "../../../../../hooks/useDebounce";
import dayjs from "dayjs";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import styles from "./Request.module.scss";
import { getStaffSelectListAction } from "../../../../../redux/services/staff/staffServices";
import { toastService } from "../../../../common/toast-messages/toast";
import ApproveRejectStatusDialog from "./ApproveRejectStatusDialog";

const Request = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  /* loading dependencies */
  const [staffListLoading, setstaffListLoading] = useState({
    status: false,
    id: null,
  });
  /* loading dependencies */

  /* form dependencies */
  const [form] = Form.useForm();
  /* form dependencies */

  /* dialog dependencies */
  const [status, setStatus] = useState("");
  const [currentRecord, setCurrentRecord] = useState();
  const [openApproveRejectStatusDialog, setOpenApproveRejectStatusDialog] =
    useState(false);
  /* dialog dependencies */

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const { serviceProviderAppointmentsInfo } = useSelector(
    (state) => state.serviceProviderAppointments
  );
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: serviceProviderAppointmentsInfo?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  /* table dependencies */

  /* select list dependencies */
  const { staffSelectListForRequests, requestStaffList } = useSelector(
    (state) => state.staff
  );
  const { servicesSelectList } = useSelector((state) => state.services);
  /* select list dependencies */

  /* filter dependencies */
  const { debouncedValue: debouncedSearchValue, debounceLoading } = useDebounce(
    searchValue,
    500
  );
  const [selectedCountryObj, setSelectedCountryObj] = useState(null);
  /* filter dependencies */

  const selectListApiCalls = () => {
    const dataToBeSentForStaffSelectList = { page: 1, limit: 500 };
    // service provider / staff select list
    dispatch(
      getStaffSelectListAction(dataToBeSentForStaffSelectList, setLoading)
    );
  };

  useEffect(() => {
    selectListApiCalls();
  }, []);

  /* function definition to get table data */
  const handleGetTableData = () => {
    dispatch(
      Actions.createAction(Actions.GET_SERVICE_PROVIDER_APPOINTMENTS_LIST, {
        serviceProviderAppointmentsInfo: {},
      })
    );
    form.resetFields();
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue,
      country: selectedCountryObj,
      // order_status: "ASSIGNED_TO_SERVICE_PROVIDER",
      service_status: ["SCHEDULED", "RECEIVED"],
      service_provider_status: ["RECEIVED", "APPROVED"],
    };
    dispatch(getServiceProviderAppointmentsAction(dataToBeSent, setLoading));
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
  }, [pagination, debouncedSearchValue]);

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("Modules.Appointments.orderNumber"),
        dataIndex: "order_number",
        key: "order_number",
      },
      {
        title: t("Modules.Appointments.orderType"),
        dataIndex: "order_type",
        key: "order_type",
      },
      {
        title: t("servicesList.title"),
        dataIndex: "service_name",
        key: "service_name",
        render: (_, record) => <p>{record?.service_name || "-"}</p>,
      },
      {
        title: t("Modules.Appointments.requestDate"),
        dataIndex: "order_place_datetime",
        key: "order_place_datetime",
        render: (_, record) => (
          <p>
            {dateObjToDispViewDate(record?.order_place_datetime, "ddmmyyyy")}
          </p>
        ),
      },
      {
        title: t("Modules.Appointments.appointmentDate"),
        dataIndex: "appointment_date",
        key: "appointment_date",
        render: (_, record) => (
          <p>{dateObjToDispViewDate(record?.appointment_date, "ddmmyyyy")}</p>
        ),
      },
      {
        title: t("Modules.Appointments.appointmentTime"),
        dataIndex: "appointment_date_time",
        key: "appointment_date_time",
        render: (_, record) => (
          <p>
            {timeObjToDispViewTimeWithSuffix(dayjs(record?.appointment_date_time).utc().local().format('ddd, MMM D, H:mm z'))}
          </p>
        ),
      },
      {
        title: t("Modules.Appointments.appointmentMode"),
        dataIndex: "appointment_mode",
        key: "appointment_mode",
      },
      {
        title: t("appointmentInfo.customerName"),
        dataIndex: "name",
        key: "name",
        render: (_, record) => (
          <p>
            {record?.booking_for_details
              ? `${record?.booking_for_details?.first_name} ${record?.booking_for_details?.last_name}`
              : "-"}
          </p>
        ),
      },
      {
        title: t("Modules.Request.RequestList.staffSelection"),
        key: "service_provider",
        dataIndex: "service_provider",
        render: (_, record, index) => {
          const tempStaff = record?.service_provider;
          let id = "";
          const order_type = record?.order_type;
          switch (order_type) {
            case "service":
              if(record?.service_provider_id !== null && record?.service_provider_id !==undefined && record?.service_provider_id !== ""){
                id = record?.service_provider_id;
                form.setFieldValue(`staff_${id}`, id);
              }
              else {
                id = `${record?._id}___${record?.service_id}`;
              }
              break;

            case "service_provider":
              id = record?.service_provider_id;
              form.setFieldValue(`staff_${id}`, id);
              break;

            case "package":
              id = `${record?._id}___${record?.service_id}`;
              break;

            default:
              break;
          }

          return (
            <>
              <Form
                style={{ marginTop: 20 }}
                form={form}
                layout="vertical"
                key={id}
              >
                <Form.Item
                  name={`staff_${id}`}
                  rules={[
                    {
                      required: true,
                      message: t(
                        "Modules.Request.RequestList.pleaseSelectStaff"
                      ),
                    },
                  ]}
                >
                  <Select
                    onClick={() => {
                      if ((record?.order_type === "package") || (record?.order_type === "service" && (record?.service_provider_id === null || record?.service_provider_id === undefined || record?.service_provider_id === ""))) {
                        dispatch(
                          getAvailableStaffForAppointmentAction(
                            {
                              orderId: record._id,
                              service_id: record?.service_id,
                              order_type: record?.order_type,
                            },
                            setstaffListLoading
                          )
                        );
                      }
                    }}
                    options={
                      record?.order_type === "service_provider" || (record?.order_type === "service" && record?.service_provider_id !== null && record?.service_provider_id !==undefined && record?.service_provider_id !== "")
                        ? staffSelectListForRequests
                        : requestStaffList
                    }
                    onBlur={() =>
                      dispatch(Actions.createAction(Actions.CLEAR_TEMP_STAFF))
                    }
                    placeholder={t("Modules.Request.RequestList.selectStaff")}
                    showSearch
                    disabled={record?.order_type === "service_provider" || (record?.order_type === "service" && record?.service_provider_id !== null && record?.service_provider_id !==undefined && record?.service_provider_id !== "")}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    defaultValue={staffSelectListForRequests?.find(
                      (element) => element?._id === id
                    )}
                  />
                </Form.Item>
              </Form>
            </>
          );
        },
      },
      {
        title: t("Modules.AutoCredit.status"),
        key: "is_payment_completed",
        dataIndex: "is_payment_completed",
        render: (_, { is_payment_completed }) => (
          <>
            <Tag
              bordered={false}
              style={{
                background: is_payment_completed
                  ? colors.lightTeal
                  : colors.lightPinkishRed,
                color: is_payment_completed
                  ? colors.darkTeal
                  : colors.darkMaroon,
              }}
            >
              {is_payment_completed ? t("Modules.Appointments.paid") : t("Modules.Appointments.due")}
            </Tag>
          </>
        ),
      },
      {
        title: t("appointmentInfo.charges"),
        dataIndex: "total_amount",
        key: "total_amount",
        render: (_, record) => (
          <p>{record?.total_amount ? record?.total_amount?.toFixed(2) : "-"}</p>
        ),
      },
      {
        title: t("appointmentInfo.action"),
        key: "action",
        align: "center",
        render: (_, record, index) => {
          let id = "";
          const order_type = record?.order_type;
          switch (order_type) {
            case "service":
              if(record?.service_provider_id !== null && record?.service_provider_id !==undefined && record?.service_provider_id !== ""){
                id = record?.service_provider_id;
                form.setFieldValue(`staff_${id}`, id);
              }
              else {
                id = `${record?._id}___${record?.service_id}`;
              }
              break;

            case "service_provider":
              id = record?.service_provider_id;
              form.setFieldValue(`staff_${id}`, id);
              break;

            case "package":
              id = `${record?._id}___${record?.service_id}`;
              break;

            default:
              break;
          }

          return (
            <>
              <Flex gap="small" justify="center">
                <Tag
                  bordered={false}
                  style={{
                    background: colors.lightTeal,
                    color: colors.darkTeal,
                    cursor: "pointer",
                    pointerEvents: record?.is_payment_completed
                      ? "visible"
                      : "none",
                  }}
                  icon={<CheckCircleFilled />}
                  onClick={(e) => {
                    if (!record?.is_payment_completed) {
                      toastService.error("Please complete the payment");
                      return;
                    }
                    handleConfirmStatus(e, record, id, "approved");
                  }}
                >
                  Approve
                </Tag>

                <Tag
                  bordered={false}
                  style={{
                    background: colors.lightPinkishRed,
                    color: colors.darkMaroon,
                    cursor: "pointer",
                    pointerEvents: record?.is_payment_completed
                      ? "visible"
                      : "none",
                  }}
                  icon={<CloseCircleFilled />}
                  onClick={(e) => {
                    if (!record?.is_payment_completed) {
                      toastService.error("Please complete the payment");
                      return;
                    }
                    handleConfirmStatus(e, record, id, "rejected");
                  }}
                >
                  Reject
                </Tag>
              </Flex>
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, [staffSelectListForRequests, requestStaffList]);
  /* initial dependencies rendering */

  /* on confirm status */
  const handleConfirmStatus = async (e, record, id, status) => {
    switch (status) {
      case "approved":
        try {
          const staffFieldName = `staff_${id}`;

          await form.validateFields([staffFieldName]);

          const staff = form.getFieldValue(staffFieldName);

          setStatus(status);
          dispatch(
            Actions.createAction(
              Actions.SELECTED_STAFF_ID_FOR_UPDATE_ORDER_STATUS,
              { selectedStaffIdForUpdateOrderStatus: staff }
            )
          );

          const selectedServiceProviderAppointmentId = record._id;
          dispatch(
            Actions.createAction(
              Actions.SELECTED_SERVICE_PROVIDER_APPOINTMENT_ID,
              { selectedServiceProviderAppointmentId }
            )
          );
          setOpenApproveRejectStatusDialog(true);
          setCurrentRecord(record);
        } catch (errorInfo) {
          toastService.error(
            t("Modules.Request.RequestList.pleaseSelectStaff")
          );
        }
        break;

      case "rejected":
        setStatus(status);
        const selectedServiceProviderAppointmentId = record._id;
        dispatch(
          Actions.createAction(
            Actions.SELECTED_SERVICE_PROVIDER_APPOINTMENT_ID,
            { selectedServiceProviderAppointmentId }
          )
        );
        setCurrentRecord(record);
        setOpenApproveRejectStatusDialog(true);
        break;

      default:
        break;
    }
  };
  /* on confirm status */

  return (
    <div className="servicesListContainer">
      <Flex vertical gap="2rem">
        <Card
                  bordered
                  hoverable
                  style={{
                    width: 300,
                    padding: 0,
                    borderRadius: 20,
                    border: `1px solid ${colors.oceanBlue}`,
                  }}
                >
              <Row gutter={[24, 24]} align="middle" justify="center">
                <Col span={6}>
                  <Button
                    type="primary"
                    shape="circle"
                    size="middle"
                    icon={<CalendarOutlined  style={{ fontSize: 22 }} />}
                    style={{ background: colors.oceanBlue }}
                  />
                </Col>
                <Col span={18}>
                  <Flex vertical>
                    <Typography.Title level={3}>
                    {t("AppModuleNames.Provider.Request")}
                    </Typography.Title>
                    <Typography.Title
                      level={2}
                      style={{ margin: 0, color: colors.DarkMint }}
                    >
                      {`${serviceProviderAppointmentsInfo?.total || 0}`}
                    </Typography.Title>
                  </Flex>
                </Col>
              </Row>
            </Card>
            
        {loading ? (
          <Spinner />
        ) : (
          <div
            style={{
              width: "100%",
              maxWidth: "100vw - 20rem",
              overflowX: "auto",
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Table
              dataSource={serviceProviderAppointmentsInfo?.data}
              columns={columns}
              pagination={paginationObj}
            />
          </div>
        )}
      </Flex>
      <ApproveRejectStatusDialog
        status={status}
        open={openApproveRejectStatusDialog}
        currentRecord={currentRecord}
        setCurrentRecord={setCurrentRecord}
        onClose={() => {
          setOpenApproveRejectStatusDialog(false);
          handleGetTableData();
        }}
      />
    </div>
  );
};

export default Request;
